import { useState, useCallback, useMemo } from 'react';
import { isValidName, isValidPhone, NAME_ERROR, PHONE_ERROR } from '../lib/validators';

export type ValidationRule<T> = {
  validate: (value: T[keyof T], values: T) => boolean;
  message: string;
};

export type FieldValidation<T> = {
  [K in keyof T]?: ValidationRule<T>[];
};

export type FieldErrors<T> = {
  [K in keyof T]?: string | null;
};

// ─── Common validation rules ─────────────────────────────────

export const rules = {
  required: (fieldName: string) => ({
    validate: (value: unknown) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') return value.trim().length > 0;
      if (typeof value === 'number') return !isNaN(value);
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    },
    message: `${fieldName} is required`,
  }),

  minLength: (fieldName: string, min: number) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string') return true;
      return value.trim().length >= min;
    },
    message: `${fieldName} must be at least ${min} characters`,
  }),

  maxLength: (fieldName: string, max: number) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string') return true;
      return value.trim().length <= max;
    },
    message: `${fieldName} must be at most ${max} characters`,
  }),

  email: (fieldName: string) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string' || !value.trim()) return true;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    message: `${fieldName} must be a valid email address`,
  }),

  // Person / party / company name: letters, spaces, hyphens, apostrophes, periods.
  personName: (fieldName: string) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string' || !value.trim()) return true; // empty → let `required` handle it
      return isValidName(value);
    },
    message: `${fieldName}: ${NAME_ERROR}`,
  }),

  // Phone: digits only, +<2-digit code><10 digits> or 11 national digits.
  phone: (fieldName: string) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string' || !value.trim()) return true; // empty → let `required` handle it
      return isValidPhone(value);
    },
    message: `${fieldName}: ${PHONE_ERROR}`,
  }),

  minNumber: (fieldName: string, min: number) => ({
    validate: (value: unknown) => {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (typeof num !== 'number' || isNaN(num)) return true;
      return num >= min;
    },
    message: `${fieldName} must be at least ${min}`,
  }),

  positiveNumber: (fieldName: string) => ({
    validate: (value: unknown) => {
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (typeof num !== 'number' || isNaN(num)) return true;
      return num > 0;
    },
    message: `${fieldName} must be greater than 0`,
  }),

  date: (fieldName: string) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string' || !value) return true;
      const date = new Date(value);
      return !isNaN(date.getTime());
    },
    message: `${fieldName} must be a valid date`,
  }),

  futureDate: (fieldName: string) => ({
    validate: (value: unknown) => {
      if (typeof value !== 'string' || !value) return true;
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date >= today;
    },
    message: `${fieldName} must be today or in the future`,
  }),

  custom: <T>(validateFn: (value: T[keyof T], values: T) => boolean, message: string) => ({
    validate: validateFn,
    message,
  }),
};

// ─── Hook ────────────────────────────────────────────────────

interface UseFormValidationOptions<T> {
  /** Validation rules for each field */
  validations: FieldValidation<T>;
  /** When true, validates on every value change (default: false - only on touch/submit) */
  validateOnChange?: boolean;
}

export function useFormValidation<T extends Record<string, unknown>>(
  values: T,
  options: UseFormValidationOptions<T>
) {
  const { validations, validateOnChange = false } = options;

  // Track which fields have been touched/blurred
  const [touched, setTouched] = useState<Set<keyof T>>(new Set());
  // Track if form has been submitted (to show all errors)
  const [submitted, setSubmitted] = useState(false);

  // Validate a single field
  const validateField = useCallback(
    (field: keyof T): string | null => {
      const fieldRules = validations[field];
      if (!fieldRules) return null;

      for (const rule of fieldRules) {
        if (!rule.validate(values[field], values)) {
          return rule.message;
        }
      }
      return null;
    },
    [values, validations]
  );

  // Get errors for all fields
  const errors = useMemo((): FieldErrors<T> => {
    const result: FieldErrors<T> = {};
    for (const field of Object.keys(validations) as (keyof T)[]) {
      const error = validateField(field);
      // Show error if: submitted OR touched OR validateOnChange
      const shouldShow = submitted || touched.has(field) || validateOnChange;
      result[field] = shouldShow ? error : null;
    }
    return result;
  }, [validations, validateField, touched, submitted, validateOnChange]);

  // Check if form is valid (regardless of touched state)
  const isValid = useMemo(() => {
    for (const field of Object.keys(validations) as (keyof T)[]) {
      if (validateField(field)) return false;
    }
    return true;
  }, [validations, validateField]);

  // Get first error message (for generic display if needed)
  const firstError = useMemo(() => {
    for (const field of Object.keys(validations) as (keyof T)[]) {
      const error = validateField(field);
      if (error) return error;
    }
    return null;
  }, [validations, validateField]);

  // Mark a field as touched (call onBlur)
  const touchField = useCallback((field: keyof T) => {
    setTouched((prev) => new Set(prev).add(field));
  }, []);

  // Mark all fields as touched and return validation result
  const validateAll = useCallback((): boolean => {
    setSubmitted(true);
    return isValid;
  }, [isValid]);

  // Reset validation state
  const resetValidation = useCallback(() => {
    setTouched(new Set());
    setSubmitted(false);
  }, []);

  // Helper to get field props (error + onBlur handler)
  const getFieldProps = useCallback(
    (field: keyof T) => ({
      error: errors[field],
      onBlur: () => touchField(field),
    }),
    [errors, touchField]
  );

  return {
    errors,
    isValid,
    firstError,
    touched,
    submitted,
    touchField,
    validateAll,
    validateField,
    resetValidation,
    getFieldProps,
  };
}
